/**
 * WeaveIn.ai Platform - Assignable Roles Hook
 * React hook for fetching organization roles that can be assigned to users for programs
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { userManagementAPI } from '@/services/api/userManagement';
import type {
  AssignableRole,
  UserProgramAssignment,
  UserProgramAssignmentCreate,
  ProgramRoleChange,
  UserEffectivePermissions,
  ProgramLead,
} from '@/types/roleTerminology';

interface UseAssignableRolesOptions {
  cboId: number;
  forProgramId?: number;
  autoFetch?: boolean;
}

interface UseAssignableRolesReturn {
  roles: AssignableRole[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch assignable organization roles for program assignments
 * Excludes admin roles (org_admin, super_admin) as they have full access
 */
export function useAssignableRoles(options: UseAssignableRolesOptions): UseAssignableRolesReturn {
  const { cboId, forProgramId, autoFetch = true } = options;

  const [roles, setRoles] = useState<AssignableRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchRoles = useCallback(async () => {
    if (!cboId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await userManagementAPI.getAssignableRoles(cboId, forProgramId);
      if (mountedRef.current) {
        setRoles(data);
      }
    } catch (err: any) {
      if (mountedRef.current) {
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch assignable roles';
        setError(errorMessage);
        console.error('Error fetching assignable roles:', err);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [cboId, forProgramId]);

  useEffect(() => {
    if (autoFetch && cboId) {
      fetchRoles();
    }
  }, [autoFetch, cboId, fetchRoles]);

  return {
    roles,
    loading,
    error,
    refetch: fetchRoles,
  };
}

// ============================================================================
// USER PROGRAM ASSIGNMENTS HOOK
// ============================================================================

interface UseUserProgramAssignmentsOptions {
  userId: number;
  cboId: number;
  autoFetch?: boolean;
}

interface UseUserProgramAssignmentsReturn {
  assignments: UserProgramAssignment[];
  loading: boolean;
  error: string | null;
  assignProgram: (assignment: UserProgramAssignmentCreate) => Promise<UserProgramAssignment | null>;
  removeAssignment: (assignmentId: number) => Promise<boolean>;
  changeRole: (assignmentId: number, roleChange: ProgramRoleChange) => Promise<UserProgramAssignment | null>;
  refetch: () => Promise<void>;
}

/**
 * Hook to manage user program assignments with enhanced role support
 */
export function useUserProgramAssignments(options: UseUserProgramAssignmentsOptions): UseUserProgramAssignmentsReturn {
  const { userId, cboId, autoFetch = true } = options;

  const [assignments, setAssignments] = useState<UserProgramAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchAssignments = useCallback(async () => {
    if (!userId || !cboId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await userManagementAPI.getUserProgramAssignments(userId, cboId);
      if (mountedRef.current) {
        setAssignments(data);
      }
    } catch (err: any) {
      if (mountedRef.current) {
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch program assignments';
        setError(errorMessage);
        console.error('Error fetching program assignments:', err);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [userId, cboId]);

  useEffect(() => {
    if (autoFetch && userId && cboId) {
      fetchAssignments();
    }
  }, [autoFetch, userId, cboId, fetchAssignments]);

  const assignProgram = async (assignment: UserProgramAssignmentCreate): Promise<UserProgramAssignment | null> => {
    setLoading(true);
    setError(null);

    try {
      const result = await userManagementAPI.assignProgramToUser(userId, cboId, assignment);
      if (mountedRef.current) {
        // Refetch to get updated list with role details
        await fetchAssignments();
      }
      return result.assignment;
    } catch (err: any) {
      if (mountedRef.current) {
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to assign program';
        setError(errorMessage);
        console.error('Error assigning program:', err);
      }
      return null;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const removeAssignment = async (assignmentId: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await userManagementAPI.removeProgramAssignment(userId, assignmentId, cboId);
      if (mountedRef.current) {
        setAssignments(prev => prev.filter(a => a.id !== assignmentId));
      }
      return true;
    } catch (err: any) {
      if (mountedRef.current) {
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to remove assignment';
        setError(errorMessage);
        console.error('Error removing assignment:', err);
      }
      return false;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const changeRole = async (assignmentId: number, roleChange: ProgramRoleChange): Promise<UserProgramAssignment | null> => {
    setLoading(true);
    setError(null);

    try {
      const result = await userManagementAPI.changeProgramRole(userId, assignmentId, cboId, roleChange);
      if (mountedRef.current) {
        // Refetch to get updated list with role details
        await fetchAssignments();
      }
      return result.assignment;
    } catch (err: any) {
      if (mountedRef.current) {
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to change role';
        setError(errorMessage);
        console.error('Error changing role:', err);
      }
      return null;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  return {
    assignments,
    loading,
    error,
    assignProgram,
    removeAssignment,
    changeRole,
    refetch: fetchAssignments,
  };
}

// ============================================================================
// USER EFFECTIVE PERMISSIONS HOOK
// ============================================================================

interface UseUserEffectivePermissionsOptions {
  userId: number;
  cboId: number;
  programId?: number;
  autoFetch?: boolean;
}

interface UseUserEffectivePermissionsReturn {
  permissions: UserEffectivePermissions | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch user's effective permissions
 */
export function useUserEffectivePermissions(options: UseUserEffectivePermissionsOptions): UseUserEffectivePermissionsReturn {
  const { userId, cboId, programId, autoFetch = true } = options;

  const [permissions, setPermissions] = useState<UserEffectivePermissions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchPermissions = useCallback(async () => {
    if (!userId || !cboId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await userManagementAPI.getUserEffectivePermissions(userId, cboId, programId);
      if (mountedRef.current) {
        setPermissions(data);
      }
    } catch (err: any) {
      if (mountedRef.current) {
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch permissions';
        setError(errorMessage);
        console.error('Error fetching effective permissions:', err);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [userId, cboId, programId]);

  useEffect(() => {
    if (autoFetch && userId && cboId) {
      fetchPermissions();
    }
  }, [autoFetch, userId, cboId, fetchPermissions]);

  return {
    permissions,
    loading,
    error,
    refetch: fetchPermissions,
  };
}

// ============================================================================
// PROGRAM LEADS HOOK
// ============================================================================

interface UseProgramLeadsOptions {
  programId: number;
  cboId: number;
  autoFetch?: boolean;
}

interface UseProgramLeadsReturn {
  leads: ProgramLead[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch program leads for a program
 */
export function useProgramLeads(options: UseProgramLeadsOptions): UseProgramLeadsReturn {
  const { programId, cboId, autoFetch = true } = options;

  const [leads, setLeads] = useState<ProgramLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchLeads = useCallback(async () => {
    if (!programId || !cboId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await userManagementAPI.getProgramLeads(programId, cboId);
      if (mountedRef.current) {
        setLeads(data);
      }
    } catch (err: any) {
      if (mountedRef.current) {
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch program leads';
        setError(errorMessage);
        console.error('Error fetching program leads:', err);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [programId, cboId]);

  useEffect(() => {
    if (autoFetch && programId && cboId) {
      fetchLeads();
    }
  }, [autoFetch, programId, cboId, fetchLeads]);

  return {
    leads,
    loading,
    error,
    refetch: fetchLeads,
  };
}

// Export all hooks
export default useAssignableRoles;
